import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export default function BriefingViewer() {
  const { projectId, briefingId } = useParams<{ projectId: string; briefingId: string }>();
  const navigate = useNavigate();
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [briefingName, setBriefingName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!briefingId) return;

    const load = async () => {
      try {
        // Fetch briefing record
        const { data: briefing, error: dbError } = await supabase
          .from('project_briefings')
          .select('*')
          .eq('id', briefingId)
          .is('deleted_at', null)
          .maybeSingle();

        if (dbError) throw dbError;
        if (!briefing) {
          setError('Briefing niet gevonden');
          setLoading(false);
          return;
        }

        setBriefingName((briefing as any).name);

        // Get public URL and fetch HTML content
        const { data: urlData } = supabase.storage
          .from('project-briefings')
          .getPublicUrl((briefing as any).file_path);

        const res = await fetch(urlData.publicUrl);
        if (!res.ok) throw new Error('Kon bestand niet laden');

        const html = await res.text();
        setHtmlContent(html);
      } catch (err: any) {
        console.error('Briefing viewer error:', err);
        setError(err.message || 'Er is een fout opgetreden');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [briefingId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background gap-4">
        <p className="text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Terug
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Minimal header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b bg-background/95 backdrop-blur-sm z-10 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/crm/projects/${projectId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Terug
        </Button>
        <span className="text-sm font-medium truncate">{briefingName}</span>
      </div>

      {/* HTML content in iframe */}
      <iframe
        srcDoc={htmlContent || ''}
        className="flex-1 w-full border-0"
        sandbox="allow-scripts allow-same-origin"
        title={briefingName}
      />
    </div>
  );
}
